import { useQuery } from '@tanstack/vue-query'
import { computed } from 'vue'
import { RouteLocationNamedRaw } from 'vue-router'

import { useUrl } from '@/composables/url-helpers'
import { useTr } from '@/composables/use-tr'
import { useI18nAddon } from '@/i18n/i18n'
import { usePersisterFn } from '@/query/persister-plugin'
import { Persisters } from '@/query/shared'
import { ROUTE_NAMES } from '@/router/constants'
import { APIGeneralInfo, MAIN_CATEGORIES, MainCategoriesType, NoAdGamConfig } from '@/types'
import { shuffle } from '@/utils/helpers'
import { useAPI } from '@/utils/requests/api-plugin'

const MAX_PROMO_CATEGORIES = 20
const PLACEHOLDER_DATA = {
    noAdsConfigInGam: {} as NoAdGamConfig,
    promoCategoriesList: [] as ShortCategory[],
    menuHighlightCategoriesList: [] as ShortCategory[],
    contentCategoriesList: [] as ShortCategory[],
    menuCategoriesList: [] as ShortCategory[],
    totalGamesCount: null,
}

export type ShortCategory = {
    id: MainCategoriesType
    title: string
    location: RouteLocationNamedRaw
}

export const HIGHLIGHTED_CATEGORY_TYPES: MainCategoriesType[] = ['trending_now', 'top_playgama', 'new']

export const useGeneralData = () => {
    const tr = useTr()
    const { currentLocale } = useI18nAddon()
    const { getCategoryRoute } = useUrl()
    const { getMainCategories } = useAPI()
    const mainData = computed(() => data.value || PLACEHOLDER_DATA)

    // persister type depends on queryKey cardinality
    const queryKey = computed(() => ['general-data', currentLocale.value])
    const persister = usePersisterFn(Persisters.LowCardinality)

    function setMainData(data: APIGeneralInfo) {
        const menuHighlightCategoriesList: ShortCategory[] = []
        const menuCategoriesList: ShortCategory[] = []
        const { categories, noAdsConfigInGam } = data
        const { totalGamesCount } = data
        categories.forEach(({ id, hru, title }) => {
            const mainCategoryId = id as MainCategoriesType
            if (MAIN_CATEGORIES.includes(mainCategoryId)) {
                if (HIGHLIGHTED_CATEGORY_TYPES.includes(mainCategoryId)) {
                    menuHighlightCategoriesList.push({
                        id: mainCategoryId,
                        title,
                        location: getCategoryRoute({ hru, type: 'category' }),
                    })
                } else {
                    menuCategoriesList.push({
                        id: mainCategoryId,
                        title,
                        location: getCategoryRoute({ hru, type: 'category' }),
                    })
                }
            }
        })

        const contentCategoriesList = [...menuHighlightCategoriesList, ...menuCategoriesList]

        const promoCategoriesList = [
            ...shuffle(menuCategoriesList.slice()).slice(0, Math.min(MAX_PROMO_CATEGORIES, menuCategoriesList.length)),
            {
                id: 'all_games' as const,
                title: tr('navigation.all_games'),
                location: { name: ROUTE_NAMES.ALL_CATEGORIES } as RouteLocationNamedRaw,
            },
        ]

        return {
            promoCategoriesList,
            menuHighlightCategoriesList,
            contentCategoriesList,
            menuCategoriesList,
            totalGamesCount,
            noAdsConfigInGam,
        }
    }
    const { data, suspense } = useQuery({
        queryKey,
        queryFn: () => getMainCategories(currentLocale.value).then((rawMainData) => setMainData(rawMainData)),
        persister,
        meta: {
            skipInCacheDetails: true,
        },
    })

    return {
        mainData,
        suspense,
    }
}
